/* see docs/EXTRACTED_COMMENTS.md#c069 */

import { toHex, toRgb } from "./colorUtils"

interface EyeDropperResult {
  sRGBHex: string
}
interface EyeDropperCtor {
  new (): { open: () => Promise<EyeDropperResult> }
}

export interface CustomColorControlOptions {
  initial: string
  onCommit: (rgb: string) => void
  /* see docs/EXTRACTED_COMMENTS.md#c070 */
  onPreview?: (rgb: string) => void
}

export function renderCustomColorControl(
  options: CustomColorControlOptions,
): HTMLElement {
  const { initial, onCommit, onPreview } = options

  const row = document.createElement("div")
  row.className = "wdc-custom"

  // --- swatch (native colour picker) ---
  const chip = document.createElement("label")
  chip.className = "wdc-custom__chip"
  chip.title = "Pick a colour"

  const native = document.createElement("input")
  native.type = "color"
  native.className = "wdc-custom__native"
  native.value = toHex(initial)
  chip.style.setProperty("--swatch", native.value)
  chip.appendChild(native)

  // --- hex text field ---
  const hex = document.createElement("input")
  hex.type = "text"
  hex.className = "wdc-custom__hex"
  hex.spellcheck = false
  hex.setAttribute("aria-label", "Hex colour")
  hex.value = native.value.toUpperCase()

  const sync = (hexValue: string) => {
    chip.style.setProperty("--swatch", hexValue)
    native.value = hexValue
    hex.value = hexValue.toUpperCase()
  }

  const commit = (raw: string) => {
    const rgb = toRgb(raw)
    if (!rgb) return
    sync(toHex(raw))
    onCommit(rgb)
  }

  // see docs/EXTRACTED_COMMENTS.md#c071
  native.addEventListener("input", () => {
    chip.style.setProperty("--swatch", native.value)
    hex.value = native.value.toUpperCase()
    const rgb = toRgb(native.value)
    if (rgb) onPreview?.(rgb)
  })
  native.addEventListener("change", () => commit(native.value))

  // see docs/EXTRACTED_COMMENTS.md#c072
  hex.addEventListener("input", () => {
    const rgb = toRgb(hex.value)
    if (!rgb) return
    const asHex = toHex(hex.value)
    chip.style.setProperty("--swatch", asHex)
    native.value = asHex
    onPreview?.(rgb)
  })
  hex.addEventListener("change", () => commit(hex.value))
  hex.addEventListener("keydown", event => {
    if (event.key === "Enter") {
      event.preventDefault()
      commit(hex.value)
    }
  })

  // see docs/EXTRACTED_COMMENTS.md#c073
  ;(row as { __setValue?: (color: string) => void }).__setValue = (color: string) =>
    sync(toHex(color))

  row.appendChild(chip)
  row.appendChild(hex)

  // --- eyedropper (progressive enhancement) ---
  const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor })
    .EyeDropper
  if (EyeDropper) {
    const eye = document.createElement("button")
    eye.type = "button"
    eye.className = "wdc-custom__eye"
    eye.title = "Pick from screen"
    eye.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M16.5 3.5 a2.5 2.5 0 0 1 4 4 l-1.8 1.8 1 1 -1.7 1.7 -1-1 -7.2 7.2 -3.6 .9 .9 -3.6 7.2 -7.2 -1-1 1.7-1.7 1 1 z"
        stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    </svg>`
    eye.addEventListener("click", () => {
      new EyeDropper()
        .open()
        .then(result => commit(result.sRGBHex))
        .catch(() => {
          /* user dismissed the eyedropper */
        })
    })
    row.appendChild(eye)
  }

  return row
}
